import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import {
	PaymentIntentResult,
	PaymentRequestOptions,
	PaymentRequestPaymentMethodEvent,
	StripeCardElementOptions,
	StripeCardExpiryElementOptions,
	StripeCardNumberElementOptions,
	StripeElementsOptions,
} from '@stripe/stripe-js';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { StripeCardCvcComponent, StripeCardExpiryComponent, StripeCardNumberComponent, StripeService } from 'ngx-stripe';
import { BehaviorSubject, finalize, Observable, scan, Subject, take, tap } from 'rxjs';
import { ConfirmCardPaymentData } from '@stripe/stripe-js/dist/stripe-js/payment-intents';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { CanMakePaymentResult } from '@stripe/stripe-js/dist/stripe-js/payment-request';
import { StripeElementChangeEvent } from '@stripe/stripe-js/dist/stripe-js/elements/base';
import { StripeElementLocale, StripeElementType } from '@stripe/stripe-js/dist/stripe-js/elements-group';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { ModalService } from '@core/services/modal/modal.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { STORAGE_NAMES } from '@root/app.config';
import { BlindData, ContactForm, SessionData } from '@root/app.interfaces';

export type CardInputs = { [K in StripeElementType]: StripeElementChangeEvent };

@Component({
	selector: 'app-pay-deposit-modal',
	templateUrl: './pay-deposit-modal.component.html',
	styleUrls: ['./pay-deposit-modal.component.scss', './pay-deposit-modal.responsive.scss'],
})
export class PayDepositModalComponent implements OnInit, OnDestroy {
	static readonly ID = crypto.randomUUID();

	@ViewChild(StripeCardNumberComponent) card: StripeCardNumberComponent;
	@ViewChild(StripeCardExpiryComponent) expiry: StripeCardExpiryComponent;
	@ViewChild(StripeCardCvcComponent) cvc: StripeCardCvcComponent;

	protected readonly CURRENT_LANG = this.translateService.currentLang as StripeElementLocale;
	protected readonly ELEMENT_OPTIONS: StripeElementsOptions = { locale: this.CURRENT_LANG };
	protected readonly PAYMENT_REQUEST_OPTIONS: PaymentRequestOptions = {
		country: 'AU',
		currency: 'aud',
		total: {
			label: 'Pay Deposit',
			amount: 0,
		},
		requestPayerName: true,
		requestPayerEmail: true,
	};
	protected readonly CARD_OPTIONS: StripeCardElementOptions = {
		style: {
			base: {
				iconColor: '#16416C',
				color: '#16416C',
				fontSize: '16px',
				'::placeholder': {
					color: '#ABABAB',
				},
			},
			invalid: {
				color: '#bf0000',
			},
		},
	};

	protected readonly cardOptions: StripeCardNumberElementOptions = this.createCardOption('card');
	protected readonly dateOptions: StripeCardExpiryElementOptions = this.createCardOption('date');

	private destroy$ = new Subject<void>();
	private cardInputsReady$ = new Subject<void>();
	protected cardInputsLoading$ = new BehaviorSubject<boolean>(true);
	protected cardInputs: CardInputs;

	protected paymentRequestLoading: boolean;
	protected paymentRequestError: string;
	protected paymentRequestButtons: CanMakePaymentResult;

	protected stripeForm: UntypedFormGroup = this.fb.group({
		name: ['', [Validators.required]],
		cardNumber: ['', [Validators.required]],
		cardExpiry: ['', [Validators.required]],
		cardCvc: ['', [Validators.required]],
	});

	protected get f() {
		return this.stripeForm.controls;
	}

	constructor(
		private fb: UntypedFormBuilder,
		private shareService: ShareService,
		private modalService: ModalService,
		private stripeService: StripeService,
		private translateService: TranslateService,
		private serverDataService: ServerDataService,
		private sessionStorageService: SessionStorageService,
		private dialogRef: MatDialogRef<PayDepositModalComponent>,
		@Inject(MAT_DIALOG_DATA) public modalData: { amount: number; contactForm: ContactForm },
	) {}

	@HostListener('document:keydown.escape')
	onEscape() {
		this.dialogRef.close(false);
	}

	ngOnInit(): void {
		this.modalService.liftUpModal(this.dialogRef);

		this.cardInputsReady$
			.pipe(
				scan((count) => count + 1, 0),
				tap((count) => this.cardInputsLoading$.next(count < 4)),
				takeUntil(this.destroy$),
			)
			.subscribe();

		this.stripeService.elements().subscribe(() => void this.createPaymentRequest());
		this.shareService.setDialogState({ id: PayDepositModalComponent.ID, isOpen: true });
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.shareService.setDialogState({ id: PayDepositModalComponent.ID, isOpen: false });
	}

	onClose(): void {
		this.dialogRef.close(false);
	}

	onLoadComponent(): void {
		this.cardInputsReady$.next();
	}

	onInputChange(event: StripeElementChangeEvent) {
		this.cardInputs = { ...this.cardInputs, [event.elementType]: event };

		const controls = ['cardNumber', 'cardExpiry', 'cardCvc'];
		const cardInputStatus = controls.every((key) => this.cardInputs?.[key]?.complete);

		if (!cardInputStatus) {
			this.paymentRequestError = '';
		}

		const cardControlByKey = this.stripeForm.get(event.elementType);
		const controlError = event.error ? { [event.error.code]: true } : event.empty ? { required: true } : null;

		cardControlByKey?.setValue(cardInputStatus || null);
		cardControlByKey?.setErrors(controlError);
	}

	onSubmit(): void {
		if (this.stripeForm.invalid) {
			return;
		}

		const confirmCardPaymentData: ConfirmCardPaymentData = {
			payment_method: {
				card: this.card.element,
				billing_details: { name: this.stripeForm.get('name').value },
			},
		};

		this.paymentRequestLoading = true;
		this.updateCardInputsDisableState(this.paymentRequestLoading);

		this.getSessionData()
			.pipe(
				switchMap((sessionData) => this.serverDataService.CreatePaymentIntent(sessionData)),
				switchMap((pi) => this.stripeService.confirmCardPayment(pi.clientSecret, confirmCardPaymentData)),
				finalize(() => {
					this.paymentRequestLoading = false;
					this.updateCardInputsDisableState(this.paymentRequestLoading);
				}),
			)
			.subscribe({
				next: this.onPaymentResultHandler.bind(this),
				error: (error: Error) => (this.paymentRequestError = error.message),
			});
	}

	onPaymentMethod(event: PaymentRequestPaymentMethodEvent): void {
		const confirmCardPaymentData: ConfirmCardPaymentData = {
			payment_method: event.paymentMethod.id,
		};

		this.getSessionData()
			.pipe(
				switchMap((sessionData) => this.serverDataService.CreatePaymentIntent(sessionData)),
				switchMap((pi) => this.stripeService.confirmCardPayment(pi.clientSecret, confirmCardPaymentData, { handleActions: false })),
			)
			.subscribe(this.onPaymentResultHandler.bind(this, event));
	}

	onPaymentResultHandler(result: PaymentIntentResult, event?: PaymentRequestPaymentMethodEvent): void {
		if (result.error) {
			console.error('Payment error:', result.error.message);
			event?.complete('fail');
			this.paymentRequestError = result.error.message;
		} else if (result.paymentIntent?.status === 'succeeded') {
			event?.complete('success');
			this.dialogRef.close(true);
		}
	}

	createCardOption(key: string): StripeCardNumberElementOptions {
		return {
			...this.CARD_OPTIONS,
			placeholder: this.translateService.instant(`modals.pay_deposit.inputs.${key}.placeholder`),
		};
	}

	updateCardInputsDisableState(status: boolean): void {
		[this.card, this.expiry, this.cvc].forEach((component) => component.update({ disabled: status }));

		status ? this.f.name.disable() : this.f.name.enable();
	}

	async createPaymentRequest(): Promise<void> {
		if (!this.stripeService) {
			return;
		}

		this.PAYMENT_REQUEST_OPTIONS.total.amount = this.modalData.amount * 100; // Need to convert the amount into cents.
		this.paymentRequestButtons = await this.stripeService.paymentRequest(this.PAYMENT_REQUEST_OPTIONS).canMakePayment();

		if (!this.paymentRequestButtons) {
			this.cardInputsReady$.next();
		}
	}

	getSessionData(): Observable<SessionData> {
		const blinds = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data) as BlindData[];

		return this.shareService.getUserTime.pipe(
			take(1),
			map((time) => ({
				action: '3DConfigurator',
				center_latitude: null,
				center_longitude: null,
				retailers: null,
				country: this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code),
				session_key: this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_session),
				embed_key: this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embed_key),
				origin: 'shop',
				blinds: blinds.filter((blind) => blind.has_name),
				iv_screenshot: this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_screenshot) || null,
				time,
				...this.modalData.contactForm,
			})),
		);
	}
}
